import { cn, getFaviconUrl } from '@heidi/utils';
import Image from 'next/image';
import { useState, type JSX } from 'react';

interface PartnerLogoProps {
  src?: string;
  brandColor?: string;
  size?: number;
  className?: string;
  /** Alt text for accessibility. Defaults to empty string for decorative images. */
  alt?: string;
  /** Source URL used to resolve a favicon when the partner logo is unavailable. */
  sourceUrl?: string;
  /**
   * Favicon resolver for that fallback. Defaults to the `@heidi/utils` one
   * (ruling 63) so this component needs nothing from `EvidenceAnswerPorts` —
   * it renders in the settings tree, the mobile source drawer and the
   * clinical-tools submenu, none of which mounts an answer-ports host.
   */
  faviconUrl?: (url: string) => string;
  /**
   * When true, the logo is a wordmark — render at fixed height with auto
   * width so the intrinsic aspect ratio is preserved instead of being
   * squashed into a square slot. Favicon fallback stays square.
   */
  wordmark?: boolean;
}

/**
 * PartnerLogo
 * - Renders a partner logo from the backend config when available
 * - Falls back to the site favicon (via Google favicon API) when the logo
 *   is missing or fails to load
 */
export function PartnerLogo({
  src,
  size = 14,
  className,
  alt = '',
  sourceUrl,
  wordmark = false,
  faviconUrl = getFaviconUrl,
}: PartnerLogoProps): JSX.Element {
  const [hasError, setHasError] = useState(false);

  const effectiveSrc =
    !src || hasError ? (sourceUrl ? faviconUrl(sourceUrl) : undefined) : src;

  if (!effectiveSrc) {
    // No logo and no source URL — render nothing (parent handles the slot)
    return (
      <span className={cn('shrink-0', className)} style={{ width: size, height: size }} />
    );
  }

  if (effectiveSrc === src && wordmark) {
    // Wordmark mode — use a plain <img> with auto width so the SVG's intrinsic
    // aspect ratio is preserved. next/image requires both width and height, so
    // it can't render this shape. Also sidesteps the images.domains allowlist
    // for partner CDN domains.
    return (
      // eslint-disable-next-line @next/next/no-img-element -- Partner wordmark logos need auto width
      <img
        src={effectiveSrc}
        alt={alt}
        style={{ height: size, width: 'auto' }}
        className={cn('shrink-0 object-contain', className)}
      />
    );
  }

  if (effectiveSrc !== src) {
    // Favicon fallback — use a plain <img> to avoid next/image domain allowlist issues
    return (
      // eslint-disable-next-line @next/next/no-img-element -- External dynamic favicons
      <img
        src={effectiveSrc}
        alt={alt}
        width={size}
        height={size}
        className={cn('shrink-0 object-contain', className)}
      />
    );
  }

  return (
    <Image
      src={effectiveSrc}
      alt={alt}
      width={size}
      height={size}
      // `.ico` favicons and small icons are best rendered unoptimized.
      // This avoids format constraints in the image optimizer and keeps output crisp.
      unoptimized
      className={cn('shrink-0 object-contain', className)}
      onError={() => setHasError(true)}
    />
  );
}
